import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardFooter } from './card';
import { Field, FieldDescription, FieldGroup, FieldLegend, FieldSet } from './field';
import { Separator } from './separator';

type FormProps = {
  id: string;
  title?: string;
  description?: string;
  className?: string;
  autoComplete?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  children?: React.ReactNode;
};

const Form: React.FC<FormProps> = ({
  id,
  title,
  description,
  className,
  autoComplete = 'off',
  onSubmit,
  children,
}) => {
  let content: React.ReactNode = null;
  let footer: React.ReactNode = null;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === FormContent) content = child;
    if (child.type === FormFooter) footer = child;
  });

  return (
    <Card className={cn('w-full', className)}>
      <CardContent>
        <form noValidate autoComplete={autoComplete} id={id} onSubmit={onSubmit}>
          <FieldGroup>
            <FieldSet>
              {title && <FieldLegend>{title}</FieldLegend>}
              {description && <FieldDescription>{description}</FieldDescription>}
              {content}
            </FieldSet>
          </FieldGroup>
        </form>
      </CardContent>
      {footer}
    </Card>
  );
};

type FormContentProps = {
  className?: string;
  children?: React.ReactNode;
};

const FormContent: React.FC<FormContentProps> = ({ className, children }) => {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-12 gap-4', className)}>
      {children}
    </div>
  );
};

type FormGroupProps = {
  title?: string;
  description?: string;
  children: React.ReactNode;
};

const FormGroup: React.FC<FormGroupProps> = ({ title, description, children }) => {
  return (
    <FieldGroup className="col-span-full">
      <Separator />s
      <FieldSet>
        {title && <FieldLegend>{title}</FieldLegend>}
        {description && <FieldDescription>{description}</FieldDescription>}
        <FormContent>{children}</FormContent>
      </FieldSet>
    </FieldGroup>
  );
};

type FormFooterProps = {
  children?: React.ReactNode;
};

const FormFooter: React.FC<FormFooterProps> = ({ children }) => {
  return (
    <CardFooter>
      <Field className="flex justify-end" orientation="horizontal">
        {children}
      </Field>
    </CardFooter>
  );
};

export { Form, FormContent, FormFooter, FormGroup };
